import React, { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

/**
 * Heart toggle. Optimistic UI; persists to /api/me/wishlist when signed-in,
 * otherwise pushes the visitor to /login first (and remembers the product).
 */
export default function WishlistButton({ productId, variant = "card", className = "" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!user) { setOn(false); return; }
    client.get("/me/wishlist").then((r) => {
      if (!alive) return;
      const data = r.data || {};
      const list = Array.isArray(data) ? data : (data.products || []);
      setOn(list.some((p) => p.id === productId));
    }).catch(() => {});
    return () => { alive = false; };
  }, [productId, user]);

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    if (!user) {
      try { sessionStorage.setItem("postLoginAddWishlist", productId); } catch { /* ignore */ }
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      if (on) {
        await client.delete(`/me/wishlist/${productId}`);
        setOn(false);
      } else {
        await client.post("/me/wishlist", { product_id: productId });
        setOn(true);
      }
    } catch {
      // revert
      setOn((s) => !s);
    } finally {
      setBusy(false);
    }
  };

  const styleByVariant = variant === "card"
    ? "absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/85 backdrop-blur-sm shadow-sm flex items-center justify-center hover:bg-white"
    : "inline-flex items-center gap-2 px-4 py-2 label-eyebrow border border-black/15 hover:border-black";

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={on ? "Remove from wishlist" : "Add to wishlist"}
      title={on ? "Saved" : "Save"}
      className={`${styleByVariant} ${className}`}
      data-testid={`wishlist-toggle-${productId}`}
    >
      <Heart
        size={variant === "card" ? 16 : 14}
        strokeWidth={1.6}
        className={on ? "fill-[#FF3B30] text-[#FF3B30]" : "text-black/65"}
      />
      {variant !== "card" && <span>{on ? "Saved" : "Save"}</span>}
    </button>
  );
}
